import React, { useState, useRef, Children, createElement } from 'react';
import { TrendingUpIcon, UsersIcon, ShieldCheckIcon, ActivityIcon, BarChart3Icon, NewspaperIcon, UtensilsIcon, BikeIcon, CoinsIcon, ArrowRightIcon, LightbulbIcon, SparklesIcon, TvIcon, DownloadIcon, CheckCircleIcon, WineIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, ReferenceLine, Label, ReferenceArea } from 'recharts';
import { ConfidenceNotification } from './ConfidenceNotification';
import { ShareButton } from './ShareButton';
import { LearnMore } from './LearnMore';
import { MethodologyAccordion } from './MethodologyAccordion';
import { DemographicCharts } from './DemographicCharts';
import { timelineData, audienceSegments, publishers, metrics, audiencePersonas } from '../utils/atlResonanceData';
export const ATLResonanceDashboard: React.FC = () => {
  const chartRef = useRef<HTMLDivElement>(null);
  const [activePhase, setActivePhase] = useState<'baseline' | 'flight' | 'post-flight' | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<{
    News: boolean;
    Food: boolean;
    Sport: boolean;
    Radio: boolean;
    Finance: boolean;
  }>({
    News: false,
    Food: false,
    Sport: false,
    Radio: false,
    Finance: false
  });
  // Gender composition data
  const genderData = [{
    label: 'Male',
    value: 53,
    color: '#1E90FF'
  }, {
    label: 'Female',
    value: 47,
    color: '#FF6B9D'
  }];
  // Age composition data
  const ageData = [{
    label: '35-44',
    value: 16,
    color: '#FFA726'
  }, {
    label: '45-54',
    value: 14,
    color: '#AB47BC'
  }, {
    label: '55-64',
    value: 22,
    color: '#EF5350'
  }, {
    label: '65+',
    value: 25,
    color: '#66BB6A'
  }, {
    label: 'Unknown',
    value: 23,
    color: '#BDBDBD'
  }];
  const toggleCategory = (category: 'News' | 'Food' | 'Sport' | 'Radio' | 'Finance') => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      y: 20,
      opacity: 0
    },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 15
      }
    }
  };
  const CustomTooltip = ({
    active,
    payload,
    label
  }: any) => {
    if (active && payload && payload.length) {
      return <div className="bg-white p-3 border border-gray-200 shadow-lg rounded-lg" data-id="element-4">
          <p className="text-sm font-semibold text-gray-900" data-id="element-5">{label}</p>
          <p className="text-sm text-[#1E90FF] mt-1" data-id="element-6">
            Index: <span className="font-bold" data-id="element-7">{payload[0].value}</span>
          </p>
          <p className="text-xs text-gray-500 mt-1" data-id="element-8">
            {payload[0].payload.phase}
          </p>
        </div>;
    }
    return null;
  };
  const downloadChart = async () => {
    if (!chartRef.current) return;
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(chartRef.current, {
        backgroundColor: '#ffffff',
        scale: 2
      });
      const link = document.createElement('a');
      link.download = 'campaign-timeline.png';
      link.href = canvas.toDataURL();
      link.click();
    } catch (error) {
      console.error('Failed to download chart:', error);
    }
  };
  return <motion.div className="space-y-8" variants={containerVariants} initial="hidden" animate="visible" data-id="element-9">
      {/* Header Section */}
      <div id="overview" className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 print:mb-8" data-id="element-10">
        <div data-id="element-11">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight" data-id="element-12">
            ATL Resonance Report: 50+ Mobiel
          </h1>
        </div>
        <div className="self-start sm:self-auto flex items-center bg-[#1E90FF]/10 text-[#1E90FF] px-4 py-2 rounded-lg print:hidden" data-id="element-13">
          <ActivityIcon className="h-4 w-4 mr-2 flex-shrink-0" data-id="element-14" />
          <span className="text-sm font-semibold whitespace-nowrap" data-id="element-15">
            Campaign Status: Completed
          </span>
        </div>
      </div>

      {/* Test Overview */}
      <motion.div variants={itemVariants} className="bg-white p-8 rounded-xl shadow-sm border border-gray-200 print:shadow-none print:border print:border-gray-300" data-id="element-16">
        <div className="flex flex-col md:flex-row items-start gap-6" data-id="element-17">
          <img src="https://cdn.magicpatterns.com/uploads/4uL6wVwXu8pHj8KH4aPKBU/overview-icon.png" alt="Brandpulse" className="h-14 w-14 flex-shrink-0" data-id="element-18" />
          <div className="space-y-4 flex-1" data-id="element-19">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900" data-id="element-20">
              Test Overview
            </h2>
            <p className="text-base text-gray-600 leading-relaxed" data-id="element-21">
              This report details the findings of an ATL Resonance test
              conducted by Brandpulse™ for 50+ Mobiel. We observed{' '}
              <span className="font-semibold text-gray-900" data-id="element-22">
                279,000+ unique individuals
              </span>{' '}
              from your target audience across three distinct phases: before,
              during, and after your TV campaign. Using a constant neutral
              stimulus, we measured how TV media pressure influenced behavioral
              engagement. The conclusion is positive: We measured a validated
              uplift of{' '}
              <span className="font-semibold text-[#1E90FF]" data-id="element-23">+33%</span> during
              the campaign (97.5% confidence). Post-flight data shows brand
              interest elevated at{' '}
              <span className="font-semibold text-[#1E90FF]" data-id="element-24">
                +14% above baseline
              </span>
              , a promising directional signal of sustained impact that warrants
              continued monitoring. Where traditional surveys ask a small group
              for their opinion, Brandpulse measures the actual behavior of the
              entire target audience, providing certainty that the campaign
              successfully activated the market and drove measurable brand lift.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Key Takeaways Summary Box */}
      <motion.div variants={itemVariants} className="bg-gradient-to-br from-[#1E90FF]/5 via-blue-50/30 to-transparent p-8 rounded-xl border-2 border-[#1E90FF]/20 print:bg-white print:border print:border-gray-300" data-id="element-25">
        <div className="flex items-center gap-3 mb-6" data-id="element-26">
          <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          delay: 0.3,
          type: 'spring',
          stiffness: 200
        }} data-id="element-27">
            <CheckCircleIcon className="h-6 w-6 text-[#1E90FF]" data-id="element-28" />
          </motion.div>
          <h3 className="text-xl md:text-2xl font-bold text-gray-900" data-id="element-29">
            Key Takeaways
          </h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-30">
          {['Campaign generated +33% uplift in brand interest (97.5% confidence)', 'Effect sustained 2 weeks post-flight at +14% (directional evidence)', 'Strongest with pension planning audience (Index 250)', 'Observed 279,000+ unique individuals across three measurement phases'].map((text, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.4 + idx * 0.1
        }} className="flex items-start gap-3" data-id="element-31">
              <CheckCircleIcon className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-32" />
              <p className="text-base text-gray-700 leading-relaxed" data-id="element-33">
                {text.split('+').map((part, i) => i === 0 ? part : <span key={i} data-id="element-34">
                      <span className="font-semibold text-gray-900" data-id="element-35">
                        +{part.split(' ')[0]}
                      </span>
                      {part.substring(part.indexOf(' '))}
                    </span>)}
              </p>
            </motion.div>)}
        </div>
      </motion.div>

      {/* Info Blocks */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 print:gap-4" data-id="element-36">
        {[{
        icon: BarChart3Icon,
        title: 'Test type',
        value: 'ATL Resonance'
      }, {
        icon: ActivityIcon,
        title: 'Period',
        periods: [{
          label: 'Baseline',
          date: 'Dec 9 - 14'
        }, {
          label: 'Flight',
          date: 'Dec 15 - 31'
        }, {
          label: 'Post-Flight',
          date: 'Jan 1 - 7'
        }]
      }, {
        icon: ShieldCheckIcon,
        title: 'Completion date',
        value: 'January 7, 2026'
      }].map((item, idx) => <motion.div key={idx} variants={itemVariants} className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 print:shadow-none print:border print:border-gray-300 h-full flex flex-col" data-id="element-37">
            <div className="flex items-center gap-3 mb-4" data-id="element-38">
              <item.icon className="h-5 w-5 text-[#1E90FF] flex-shrink-0" data-id="element-39" />
              <h3 className="text-base font-semibold text-gray-900" data-id="element-40">
                {item.title}
              </h3>
            </div>
            {'periods' in item ? <div className="space-y-2.5" data-id="element-41">
                {item.periods.map((period, i) => <div key={i} className="flex items-center justify-between text-sm" data-id="element-42">
                    <span className="text-gray-600" data-id="element-43">{period.label}</span>
                    <span className="font-medium text-gray-900" data-id="element-44">
                      {period.date}
                    </span>
                  </div>)}
              </div> : <p className="text-base text-gray-700" data-id="element-45">{item.value}</p>}
          </motion.div>)}
      </div>

      {/* Commercial Video */}
      <motion.div variants={itemVariants} className="bg-white p-8 rounded-xl shadow-sm border border-gray-200 print:shadow-none print:border print:border-gray-300" data-id="element-46">
        <div className="flex items-center gap-3 mb-8" data-id="element-47">
          <TvIcon className="h-6 w-6 text-[#1E90FF]" data-id="element-48" />
          <h2 className="text-xl md:text-2xl font-bold text-gray-900" data-id="element-49">
            TV Commercial
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-50">
          <div className="w-full" data-id="element-51">
            <div className="relative w-full rounded-xl overflow-hidden bg-gray-100 shadow-md" style={{
            paddingBottom: '56.25%'
          }} data-id="element-52">
              <iframe className="absolute top-0 left-0 w-full h-full print:hidden" src="https://www.youtube.com/embed/PQAaRP0O5Uc" title="50+ Mobiel TV Commercial" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-id="element-53" />
              <div className="hidden print:flex absolute inset-0 items-center justify-center text-gray-500 text-sm" data-id="element-54">
                Video: 50+ Mobiel Campaign
              </div>
            </div>
          </div>

          <div className="space-y-4" data-id="element-55">
            <h3 className="text-lg md:text-xl font-bold text-gray-900" data-id="element-56">
              50+ Mobiel: "Eindejaarsknallers"
            </h3>
            <p className="text-base text-gray-600 leading-relaxed" data-id="element-57">
              This TV commercial aired during the flight period (Dec 15 - Dec
              31) across major Dutch networks. The creative highlights an
              irresistible New Year deal: first 6 months for just 1 EUR with a
              2-year subscription, leaving you with money for all the fun things
              you want to do.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Key Metrics Grid */}
      <div id="metrics" className="grid grid-cols-1 md:grid-cols-3 gap-6 print:gap-4" data-id="element-58">
        {[{
        icon: UsersIcon,
        title: 'Unique Individuals',
        value: metrics.humanSignals.toLocaleString(),
        subtitle: 'Observed across all phases',
        plainText: 'Real people observed, not survey respondents',
        border: 'border-gray-200',
        learnMoreTitle: 'What are Unique Individuals?',
        learnMoreContent: <>
                <p className="mb-3" data-id="element-59">
                  Unique Individuals represent verified, distinct people
                  observed in their natural viewing environment across our
                  premium publisher network. This is the total number of people
                  we analyzed across the baseline, flight, and post-flight
                  periods.
                </p>
                <p className="mb-3" data-id="element-60">
                  <span className="font-semibold" data-id="element-61">Important distinction:</span>{' '}
                  These are not "people who saw your TV ad" (we cannot track
                  that). These are individuals from your target audience whose
                  behavioral responses we measured using a constant neutral
                  stimulus across all three phases.
                </p>
                <p data-id="element-62">
                  Where traditional surveys ask a small group for their opinion,
                  Brandpulse observes the actual behavior of 279,000+
                  individuals, providing statistical power to detect even subtle
                  shifts in brand engagement.
                </p>
              </>
      }, {
        icon: TrendingUpIcon,
        iconColor: 'text-green-600',
        title: 'Resonance Score',
        value: `+${metrics.uplift}%`,
        subtitle: '↑ VS industry benchmark (8-15%)',
        subtitleColor: 'text-green-600',
        plainText: '33% more brand engagement vs. baseline (100) during campaign',
        border: 'border-[#1E90FF]',
        borderWidth: 'border-2',
        highlight: true,
        learnMoreTitle: 'Understanding the Uplift',
        learnMoreContent: <>
                <p className="mb-3" data-id="element-63">
                  A +33% uplift means your campaign generated 33% more brand
                  engagement compared to the baseline period. This is measured
                  through actual behavioral responses to a constant neutral
                  stimulus across all measurement phases.
                </p>
                <p className="mb-3" data-id="element-64">
                  <span className="font-semibold" data-id="element-65">What is the baseline?</span>{' '}
                  We compare the baseline response rate (pre-campaign) against
                  the flight response rate (during campaign) to calculate the
                  pure behavioral lift caused by your TV media pressure. The
                  baseline (Index 100) represents normal, pre-campaign
                  engagement levels.
                </p>
                <p className="mb-3" data-id="element-66">
                  <span className="font-semibold" data-id="element-67">Industry Context:</span>{' '}
                  Research shows typical TV campaigns achieve 8-15% brand
                  awareness lift. Your +33% uplift is 2.2x above the industry
                  average, placing this campaign in the top performance tier and
                  demonstrating exceptional effectiveness.
                </p>
                <p data-id="element-68">
                  <span className="font-semibold" data-id="element-69">Think of it this way:</span>{' '}
                  The constant stimulus is like a thermometer measuring
                  temperature. Your TV campaign is the heat source. The uplift
                  shows how much your TV ads "heated up" brand interest in the
                  market.
                </p>
              </>
      }, {
        icon: ShieldCheckIcon,
        title: 'Lift Certainty',
        value: `${metrics.confidence}%`,
        subtitle: 'for campaign-period uplift',
        plainText: "We're 97.5% certain your campaign caused the +33% uplift",
        border: 'border-gray-200',
        learnMoreTitle: 'Understanding Confidence Levels',
        learnMoreContent: <>
                <p className="mb-3" data-id="element-70">
                  Using Bayesian statistical analysis, we can say with{' '}
                  <span className="font-semibold" data-id="element-71">97.5% certainty</span> that
                  your TV campaign caused the observed +33% uplift during the
                  flight period, rather than random chance or external factors.
                  This is a very high level of confidence, giving you strong
                  evidence that your TV investment directly drove measurable
                  results.
                </p>
                <p className="mb-3" data-id="element-72">
                  <span className="font-semibold" data-id="element-73">Campaign Period (+33%):</span>{' '}
                  97.5% confidence, statistically validated and board-ready.
                </p>
                <p className="mb-3" data-id="element-74">
                  <span className="font-semibold" data-id="element-75">
                    Post-Flight Period (+14%):
                  </span>{' '}
                  Strong directional evidence, but the 2-week observation window
                  is shorter than ideal for statistical validation (larger
                  margin of error).
                </p>
                <p className="text-xs text-gray-600 bg-amber-50 p-3 rounded-lg" data-id="element-76">
                  <span className="font-semibold" data-id="element-77">For future tests:</span>{' '}
                  Extending the post-flight observation to 3-4 weeks would
                  provide the same 97.5% confidence for sustained impact claims.
                  This can be configured when setting up your next ATL Resonance
                  test.
                </p>
              </>
      }].map((metric, idx) => <motion.div key={idx} variants={itemVariants} className={`bg-white p-6 rounded-xl shadow-sm ${metric.borderWidth || 'border'} ${metric.border} print:shadow-none print:border print:border-gray-300 flex flex-col h-full`} data-id="element-78">
            <div className="flex items-center gap-3 mb-4" data-id="element-79">
              <metric.icon className={`h-5 w-5 ${metric.iconColor || 'text-gray-600'} flex-shrink-0`} data-id="element-80" />
              <h3 className="text-base font-semibold text-gray-900" data-id="element-81">
                {metric.title}
              </h3>
            </div>
            <motion.p initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.5 + idx * 0.1,
          type: 'spring',
          stiffness: 150
        }} className="text-3xl md:text-4xl font-bold text-gray-900 mb-2" data-id="element-82">
              {metric.value}
            </motion.p>
            <p className={`text-sm ${metric.subtitleColor || 'text-gray-500'} mb-2`} data-id="element-83">
              {metric.subtitle && metric.subtitle.includes('•') ? <>
                  {metric.subtitle.split('•')[0]}
                  <span className="text-gray-400" data-id="element-84">
                    • {metric.subtitle.split('•')[1]}
                  </span>
                </> : metric.subtitle}
            </p>
            <p className="text-xs text-gray-400 italic mb-4 flex-grow" data-id="element-85">
              In plain terms: {metric.plainText}
            </p>
            <div className="mt-auto pt-4 border-t border-gray-50" data-id="element-86">
              <LearnMore title={metric.learnMoreTitle} data-id="element-87">
                {metric.learnMoreContent}
              </LearnMore>
            </div>
          </motion.div>)}
      </div>

      {/* Timeline Visualization */}
      <motion.div id="timeline" variants={itemVariants} className="bg-white p-8 rounded-xl shadow-sm border border-gray-200 print:shadow-none print:border print:border-gray-300 print:break-inside-avoid" data-id="element-88">
        <div className="mb-6" data-id="element-89">
          <p className="text-sm text-gray-500 leading-relaxed" data-id="element-90">
            <span className="font-semibold text-gray-700" data-id="element-91">
              Timeline Analysis:
            </span>{' '}
            Visualizing the correlation between media flight dates and brand
            interest peaks.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-8 gap-6" data-id="element-92">
          <div data-id="element-93">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2" data-id="element-94">
              The Heartbeat: Campaign Timeline
            </h2>
            <p className="text-sm text-gray-500" data-id="element-95">
              Visualizing user interest before, during, and after the TV flight
            </p>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4" data-id="element-96">
            <button onClick={downloadChart} className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#1E90FF] transition-colors print:hidden self-start sm:self-auto px-4 py-2 rounded-lg hover:bg-gray-50" type="button" data-id="element-97">
              <DownloadIcon className="h-4 w-4" data-id="element-98" />
              <span data-id="element-99">Download Chart</span>
            </button>
            <div className="flex flex-wrap items-center gap-4 text-sm print:space-x-3" data-id="element-100">
              {[{
              phase: 'baseline',
              label: 'Baseline (Dec 9 - 14)',
              color: 'bg-gray-300'
            }, {
              phase: 'flight',
              label: 'Flight (Dec 15 - 31)',
              color: 'bg-[#1E90FF]'
            }, {
              phase: 'post-flight',
              label: 'Post-Flight (Jan 1 - 7)',
              color: 'bg-blue-300'
            }].map(({
              phase,
              label,
              color
            }) => <div key={phase} className="flex items-center cursor-pointer transition-opacity duration-200 print:cursor-default" style={{
              opacity: activePhase === null || activePhase === phase ? 1 : 0.3
            }} onMouseEnter={() => setActivePhase(phase as any)} onMouseLeave={() => setActivePhase(null)} data-id="element-101">
                  <div className={`w-3 h-3 ${color} rounded-full mr-2 flex-shrink-0`} data-id="element-102"></div>
                  <span className="text-gray-600 font-medium whitespace-nowrap" data-id="element-103">
                    {label}
                  </span>
                </div>)}
            </div>
          </div>
        </div>

        <div ref={chartRef} className="h-[350px] md:h-[450px] w-full relative print:h-[350px]" data-id="element-104">
          <div className="absolute inset-0 pointer-events-none z-10 print:hidden" data-id="element-105">
            <div className="h-full flex" data-id="element-106">
              <div className="pointer-events-auto cursor-pointer" style={{
              width: '20%'
            }} onMouseEnter={() => setActivePhase('baseline')} onMouseLeave={() => setActivePhase(null)} data-id="element-107" />
              <div className="pointer-events-auto cursor-pointer" style={{
              width: '56.67%'
            }} onMouseEnter={() => setActivePhase('flight')} onMouseLeave={() => setActivePhase(null)} data-id="element-108" />
              <div className="pointer-events-auto cursor-pointer" style={{
              width: '23.33%'
            }} onMouseEnter={() => setActivePhase('post-flight')} onMouseLeave={() => setActivePhase(null)} data-id="element-109" />
            </div>
          </div>

          <div className="absolute top-[58%] right-[8%] z-20 hidden md:block pointer-events-none" data-id="element-110">
            <span className="text-[#1E90FF] font-bold text-sm bg-white/90 px-2 py-1 rounded shadow-sm border border-blue-100" data-id="element-111">
              +14% sustained
            </span>
          </div>

          <ResponsiveContainer width="100%" height="100%" data-id="element-112">
            <AreaChart data={timelineData} margin={{
            top: 20,
            right: 10,
            left: -10,
            bottom: 0
          }} data-id="element-113">
              <defs data-id="element-114">
                <linearGradient id="colorIndex" x1="0" y1="0" x2="0" y2="1" data-id="element-115">
                  <stop offset="5%" stopColor="#1E90FF" stopOpacity={0.3} data-id="element-116" />
                  <stop offset="95%" stopColor="#1E90FF" stopOpacity={0} data-id="element-117" />
                </linearGradient>
                <linearGradient id="colorIndexHighlight" x1="0" y1="0" x2="0" y2="1" data-id="element-118">
                  <stop offset="5%" stopColor="#1E90FF" stopOpacity={0.5} data-id="element-119" />
                  <stop offset="95%" stopColor="#1E90FF" stopOpacity={0.1} data-id="element-120" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" data-id="element-121" />
              <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{
              fill: '#6B7280',
              fontSize: 11,
              fontWeight: 500
            }} dy={10} interval="preserveStartEnd" data-id="element-122" />
              <YAxis domain={[80, 160]} axisLine={false} tickLine={false} tick={{
              fill: '#6B7280',
              fontSize: 11,
              fontWeight: 500
            }} label={{
              value: 'Interest Index',
              angle: -90,
              position: 'insideLeft',
              style: {
                fill: '#6B7280',
                fontSize: 11,
                fontWeight: 500
              }
            }} data-id="element-123" />
              <RechartsTooltip content={<CustomTooltip data-id="element-125" />} data-id="element-124" />

              {activePhase === 'baseline' && <ReferenceArea x1="Dec 9" x2="Dec 15" y1={80} y2={160} fill="#D1D5DB" fillOpacity={0.15} stroke="none" data-id="element-126" />}
              {activePhase === 'flight' && <ReferenceArea x1="Dec 15" x2="Jan 1" y1={80} y2={160} fill="#1E90FF" fillOpacity={0.12} stroke="none" data-id="element-127" />}
              {activePhase === 'post-flight' && <ReferenceArea x1="Jan 1" x2="Jan 7" y1={80} y2={160} fill="#93C5FD" fillOpacity={0.15} stroke="none" data-id="element-128" />}

              <ReferenceLine x="Dec 15" stroke="#9CA3AF" strokeDasharray="3 3" strokeWidth={1.5} data-id="element-129">
                <Label value="Launch" position="top" fill="#6B7280" fontSize={10} fontWeight={600} data-id="element-130" />
              </ReferenceLine>
              <ReferenceLine x="Jan 1" stroke="#9CA3AF" strokeDasharray="3 3" strokeWidth={1.5} data-id="element-131">
                <Label value="End" position="top" fill="#6B7280" fontSize={10} fontWeight={600} data-id="element-132" />
              </ReferenceLine>
              <ReferenceLine y={100} stroke="#EF4444" strokeDasharray="3 3" strokeWidth={1.5} data-id="element-133">
                <Label value="Baseline" position="insideTopRight" fill="#EF4444" fontSize={10} fontWeight={600} data-id="element-134" />
              </ReferenceLine>

              <ReferenceArea x1="Dec 15" x2="Dec 31" y1={100} y2={133} fill="transparent" stroke="none" data-id="element-135">
                <Label value="+33%" position="center" fill="#1E90FF" fontSize={16} fontWeight="bold" data-id="element-136" />
              </ReferenceArea>

              <Area type="monotone" dataKey="index" stroke="#1E90FF" strokeWidth={3} fillOpacity={1} fill={activePhase ? 'url(#colorIndexHighlight)' : 'url(#colorIndex)'} animationDuration={2000} baseValue={100} data-id="element-137" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="mt-6 p-6 bg-blue-50/30 rounded-xl border border-blue-100 print:hidden" data-id="element-138">
          <div className="flex items-center gap-3 mb-4" data-id="element-139">
            <LightbulbIcon className="h-5 w-5 text-[#1E90FF]" data-id="element-140" />
            <h3 className="text-lg font-bold text-gray-900" data-id="element-141">Key Insights</h3>
          </div>

          <div className="space-y-4" data-id="element-142">
            {[{
            icon: TrendingUpIcon,
            color: 'text-[#1E90FF]',
            bg: 'bg-white',
            border: 'border-blue-200',
            title: 'Validated Campaign Uplift: +33% (97.5% Confidence)',
            text: 'The campaign generated a statistically validated +33% uplift in brand engagement during the active flight period (Dec 15 - Jan 1). This represents board-ready proof that your TV investment drove measurable market activation. Industry research shows typical TV campaigns achieve 8-15% brand awareness lift. Your +33% uplift is 2.2x above the industry average, placing this campaign in the top performance tier.'
          }, {
            icon: SparklesIcon,
            color: 'text-[#1E90FF]',
            bg: 'bg-gradient-to-r from-blue-50/50 to-cyan-50/30',
            border: 'border-blue-200',
            title: 'Instant Traction: Creative Validation',
            text: 'We detected zero "warm-up period". The curve spiked immediately at the start of the flight. This validates that the creative message ("Eindejaarsknallers") was extremely clear and urgent to the target audience, a strong compliment to the creative execution.'
          }, {
            icon: CoinsIcon,
            color: 'text-emerald-600',
            bg: 'bg-gradient-to-r from-emerald-50/50 to-green-50/30',
            border: 'border-emerald-200',
            title: 'Promising Sustained Effect: +14% (Directional Evidence)',
            text: 'The campaign delivered more than just temporary activation. We observe brand engagement sustained for 2 weeks at +14% above baseline, a promising indicator of lasting brand equity.',
            note: 'This post-flight effect represents strong directional evidence but has not yet reached statistical validation due to the shorter 2-week observation window. Extending post-flight measurement to 3-4 weeks in future tests would provide validated certainty on sustained impact.'
          }].map((insight, idx) => <div key={idx} className={`p-5 ${insight.bg} rounded-xl border ${insight.border}`} data-id="element-143">
                <div className="flex items-start gap-4" data-id="element-144">
                  <insight.icon className={`h-5 w-5 ${insight.color} flex-shrink-0 mt-0.5`} data-id="element-145" />
                  <div className="flex-1" data-id="element-146">
                    <p className="text-base font-bold text-gray-900 mb-2" data-id="element-147">
                      {insight.title}
                    </p>
                    <p className="text-sm text-gray-600 leading-relaxed mb-3" data-id="element-148">
                      {insight.text}
                    </p>
                    {insight.note && <p className="text-xs text-gray-600 bg-white p-3 rounded-lg border border-emerald-200" data-id="element-149">
                        <span className="font-semibold" data-id="element-150">Note:</span>{' '}
                        {insight.note}
                      </p>}
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </motion.div>

      {/* Methodology Accordion */}
      <MethodologyAccordion data-id="element-151" />

      {/* Study Scope: Analyzed Demographics */}
      <motion.div variants={itemVariants} className="bg-white p-8 rounded-xl shadow-sm border border-gray-200 print:shadow-none print:border print:border-gray-300" data-id="element-152">
        <div className="mb-8" data-id="element-153">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2" data-id="element-154">
            Study Scope: Analyzed Demographics
          </h2>
          <p className="text-sm text-gray-500" data-id="element-155">
            Distribution of the 279,000+ unique individuals analyzed based on
            impressions
          </p>
        </div>

        <DemographicCharts genderData={genderData} ageData={ageData} data-id="element-156" />

        <div className="mt-8 p-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-157">
          <div className="flex items-start gap-3" data-id="element-158">
            <UsersIcon className="h-5 w-5 text-[#1E90FF] flex-shrink-0 mt-0.5" data-id="element-159" />
            <p className="text-sm text-gray-600 leading-relaxed" data-id="element-160">
              <span className="font-semibold text-gray-900" data-id="element-161">Key Finding:</span>{' '}
              The study successfully captured a mature audience, with the
              largest identified segment being{' '}
              <span className="font-semibold text-gray-900" data-id="element-162">65+ (25%)</span>,
              followed closely by{' '}
              <span className="font-semibold text-gray-900" data-id="element-163">55-64 (22%)</span>.
              This demographic distribution aligns perfectly with the 50+ Mobiel
              target market, validating the precision of our audience
              measurement approach.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Campaign Impact: Demographic Uplift */}
      <motion.div variants={itemVariants} className="bg-white p-8 rounded-xl shadow-sm border border-gray-200 print:shadow-none print:border print:border-gray-300" data-id="element-164">
        <div className="mb-8" data-id="element-165">
          <p className="text-sm text-gray-500 leading-relaxed mb-4" data-id="element-166">
            <span className="font-semibold text-gray-700" data-id="element-167">
              Campaign Impact:
            </span>{' '}
            Measuring engagement uplift across demographic segments compared to
            pre-campaign baseline (Index 100 = baseline engagement level).
          </p>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-id="element-168">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900" data-id="element-169">
              Campaign Impact: Demographic Uplift
            </h2>
            <span className="self-start sm:self-auto text-xs font-bold text-[#1E90FF] bg-[#1E90FF]/10 px-3 py-1.5 rounded-full" data-id="element-170">
              Uplift Index
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-171">
          {/* Gender Uplift */}
          <div data-id="element-172">
            <h3 className="text-lg font-bold text-gray-900 mb-6" data-id="element-173">
              Gender Uplift
            </h3>
            <div className="space-y-6" data-id="element-174">
              {[{
              name: 'Male',
              index: 143,
              label: 'High Impact',
              isTop: true
            }, {
              name: 'Female',
              index: 104,
              label: 'Stable',
              isTop: false
            }].map((segment, idx) => <div key={idx} data-id="element-175">
                  <div className="flex items-center justify-between mb-2" data-id="element-176">
                    <div className="flex items-center gap-2" data-id="element-177">
                      <span className="text-sm font-bold text-gray-900" data-id="element-178">
                        {segment.name}
                      </span>
                      {segment.isTop && <span className="text-[10px] font-bold text-white bg-[#1E90FF] px-2 py-0.5 rounded-full" data-id="element-179">
                          HIGH IMPACT
                        </span>}
                    </div>
                    <span className={`text-sm font-bold ${segment.isTop ? 'text-[#1E90FF]' : 'text-gray-500'}`} data-id="element-180">
                      Index: {segment.index}
                    </span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden" data-id="element-181">
                    <motion.div className={`h-3 rounded-full ${segment.isTop ? 'bg-[#1E90FF]' : 'bg-gray-400'}`} initial={{
                  width: 0
                }} animate={{
                  width: `${segment.index / 150 * 100}%`
                }} transition={{
                  duration: 1,
                  delay: 0.5 + idx * 0.1,
                  ease: 'easeOut'
                }} data-id="element-182" />
                  </div>
                  <div className="flex items-center justify-between mt-2" data-id="element-183">
                    <p className="text-xs text-gray-500 font-medium" data-id="element-184">
                      {segment.index > 100 ? `${segment.index - 100}% above baseline` : segment.index === 100 ? 'At baseline' : 'Below baseline'}
                    </p>
                    <p className="text-xs text-gray-400 italic" data-id="element-185">
                      {segment.label}
                    </p>
                  </div>
                </div>)}
            </div>

            <div className="mt-8 p-4 bg-blue-50/30 rounded-lg border border-blue-100" data-id="element-186">
              <div className="flex items-start gap-3" data-id="element-187">
                <TrendingUpIcon className="h-5 w-5 text-[#1E90FF] mt-0.5 flex-shrink-0" data-id="element-188" />
                <p className="text-sm text-gray-600 leading-relaxed" data-id="element-189">
                  <span className="font-semibold text-gray-900" data-id="element-190">
                    Key Insight:
                  </span>{' '}
                  The TV campaign had the strongest activation effect on{' '}
                  <span className="font-semibold text-[#1E90FF]" data-id="element-191">Men</span>.
                  While women had high baseline interest, Men showed a{' '}
                  <span className="font-semibold text-gray-900" data-id="element-192">
                    43% increase
                  </span>{' '}
                  in engagement triggered by the flight.
                </p>
              </div>
            </div>
          </div>

          {/* Age Uplift */}
          <div data-id="element-193">
            <h3 className="text-lg font-bold text-gray-900 mb-6" data-id="element-194">Age Uplift</h3>
            <div className="space-y-6" data-id="element-195">
              {[{
              name: '65+',
              index: 125,
              label: 'Top Driver',
              isTop: true
            }, {
              name: '55-64',
              index: 115,
              label: 'Strong',
              isTop: false
            }, {
              name: '35-54',
              index: 105,
              label: 'Moderate',
              isTop: false
            }].map((segment, idx) => <div key={idx} data-id="element-196">
                  <div className="flex items-center justify-between mb-2" data-id="element-197">
                    <div className="flex items-center gap-2" data-id="element-198">
                      <span className="text-sm font-bold text-gray-900" data-id="element-199">
                        {segment.name}
                      </span>
                      {segment.isTop && <span className="text-[10px] font-bold text-white bg-[#1E90FF] px-2 py-0.5 rounded-full" data-id="element-200">
                          TOP DRIVER
                        </span>}
                    </div>
                    <span className={`text-sm font-bold ${segment.isTop ? 'text-[#1E90FF]' : 'text-gray-500'}`} data-id="element-201">
                      Index: {segment.index}
                    </span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden" data-id="element-202">
                    <motion.div className={`h-3 rounded-full ${segment.isTop ? 'bg-[#1E90FF]' : 'bg-gray-400'}`} initial={{
                  width: 0
                }} animate={{
                  width: `${segment.index / 150 * 100}%`
                }} transition={{
                  duration: 1,
                  delay: 0.5 + idx * 0.1,
                  ease: 'easeOut'
                }} data-id="element-203" />
                  </div>
                  <div className="flex items-center justify-between mt-2" data-id="element-204">
                    <p className="text-xs text-gray-500 font-medium" data-id="element-205">
                      {segment.index > 100 ? `${segment.index - 100}% above baseline` : 'At baseline'}
                    </p>
                    <p className="text-xs text-gray-400 italic" data-id="element-206">
                      {segment.label}
                    </p>
                  </div>
                </div>)}
            </div>

            <div className="mt-8 p-4 bg-blue-50/30 rounded-lg border border-blue-100" data-id="element-207">
              <div className="flex items-start gap-3" data-id="element-208">
                <TrendingUpIcon className="h-5 w-5 text-[#1E90FF] mt-0.5 flex-shrink-0" data-id="element-209" />
                <p className="text-sm text-gray-600 leading-relaxed" data-id="element-210">
                  <span className="font-semibold text-gray-900" data-id="element-211">
                    Key Insight:
                  </span>{' '}
                  The campaign successfully activated the older target
                  demographics, showing the highest uplift among the{' '}
                  <span className="font-semibold text-[#1E90FF]" data-id="element-212">
                    65+ segment
                  </span>{' '}
                  with a{' '}
                  <span className="font-semibold text-gray-900" data-id="element-213">
                    25% increase
                  </span>{' '}
                  above baseline.
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Statistical Proof & Audience Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 print:gap-6" data-id="element-214">
        {/* Statistical Proof Column */}
        <motion.div id="proof" variants={itemVariants} className="space-y-8 flex flex-col" data-id="element-215">
          <div className="flex flex-col gap-6" data-id="element-216">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900" data-id="element-217">
              Statistical Proof
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-218">
              {[{
              icon: ShieldCheckIcon,
              title: 'Lift Certainty',
              value: '97.5%',
              desc: 'Probability that the campaign uplift during flight was caused by the campaign and not by chance.'
            }, {
              icon: BarChart3Icon,
              title: 'Statistical Validation',
              value: 'p < 0.05',
              desc: 'The uplift is statistically validated, not due to random chance.'
            }].map((stat, idx) => <div key={idx} className="bg-[#1E90FF]/5 p-6 rounded-xl border border-[#1E90FF]/20 print:bg-white print:border print:border-gray-300 h-full" data-id="element-219">
                  <div className="flex items-center gap-3 mb-4" data-id="element-220">
                    <stat.icon className="h-5 w-5 text-[#1E90FF]" data-id="element-221" />
                    <h3 className="text-base font-semibold text-gray-900" data-id="element-222">
                      {stat.title}
                    </h3>
                  </div>
                  <p className="text-2xl font-bold text-gray-900 mb-3" data-id="element-223">
                    {stat.value}
                  </p>
                  <p className="text-sm text-gray-600 leading-relaxed" data-id="element-224">
                    {stat.desc}
                  </p>
                </div>)}
            </div>
          </div>

          {/* Premium Publisher Showcase */}
          <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-200 print:shadow-none print:border print:border-gray-300 flex-1" data-id="element-225">
            <div className="mb-8" data-id="element-226">
              <div className="flex items-center gap-3 mb-4" data-id="element-227">
                <h2 className="text-xl font-bold text-gray-900" data-id="element-228">
                  Premium Measurement Environment
                </h2>
                <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                delay: 0.8,
                type: 'spring',
                stiffness: 200
              }} data-id="element-229">
                  <CheckCircleIcon className="h-5 w-5 text-green-600" data-id="element-230" />
                </motion.div>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-231">
                Measurement took place at the heart of Dutch quality publishers.
                We see a stable presence on A-brands such as RTL, IEX, and major
                lifestyle titles, which guarantees that the measured resonance
                comes from real, engaged consumers.
              </p>
            </div>

            {/* Category Groups */}
            <div className="space-y-6" data-id="element-232">
              {[{
              key: 'News',
              label: 'News & Current Affairs',
              badge: 'Trusted Information',
              color: 'bg-[#1E90FF]/10 text-[#1E90FF]',
              icon: NewspaperIcon,
              iconColor: 'text-[#1E90FF]'
            }, {
              key: 'Food',
              label: 'Food & Lifestyle',
              badge: 'High Engagement',
              color: 'bg-emerald-50 text-emerald-700',
              icon: UtensilsIcon,
              iconColor: 'text-emerald-600'
            }, {
              key: 'Sport',
              label: 'Sport & Automotive',
              badge: 'Active 50+ Male',
              color: 'bg-amber-50 text-amber-700',
              icon: BikeIcon,
              iconColor: 'text-amber-600'
            }, {
              key: 'Radio',
              label: 'Radio & Weather',
              badge: 'Contextual Relevance',
              color: 'bg-blue-50 text-blue-700',
              icon: ActivityIcon,
              iconColor: 'text-blue-600'
            }, {
              key: 'Finance',
              label: 'Financial',
              badge: 'Affluent Audience',
              color: 'bg-purple-50 text-purple-700',
              icon: CoinsIcon,
              iconColor: 'text-purple-600'
            }].map(category => <div key={category.key} data-id="element-233">
                  <div className="flex items-center gap-2 mb-3" data-id="element-234">
                    <div className={`px-3 py-1.5 ${category.color} rounded-lg`} data-id="element-235">
                      <span className="text-xs font-bold uppercase tracking-wide" data-id="element-236">
                        {category.label}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500" data-id="element-237">
                      {category.badge}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 gap-3" data-id="element-238">
                    {publishers.filter(pub => pub.category === category.key).slice(0, expandedCategories[category.key as keyof typeof expandedCategories] ? undefined : 2).map((pub, idx) => <motion.div key={idx} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 0.9 + idx * 0.05
                }} className="bg-gradient-to-br from-gray-50 to-white p-4 rounded-lg border border-gray-200 print:bg-white" data-id="element-239">
                          <div className="flex items-center gap-3" data-id="element-240">
                            <div className="p-2 bg-white rounded-lg shadow-sm border border-gray-100 print:shadow-none flex-shrink-0" data-id="element-241">
                              <category.icon className={`h-5 w-5 ${category.iconColor}`} data-id="element-242" />
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-243">
                              <p className="text-sm font-bold text-gray-900 truncate" data-id="element-244">
                                {pub.name}
                              </p>
                              <p className="text-xs text-gray-500 truncate" data-id="element-245">
                                {pub.description}
                              </p>
                            </div>
                          </div>
                        </motion.div>)}
                  </div>
                  {publishers.filter(pub => pub.category === category.key).length > 2 && <button onClick={() => toggleCategory(category.key as any)} className={`mt-3 flex items-center gap-1 text-sm ${category.color.split(' ')[1]} hover:opacity-80 transition-opacity print:hidden`} data-id="element-246">
                      <span data-id="element-247">
                        {expandedCategories[category.key as keyof typeof expandedCategories] ? 'Show less' : `Show ${publishers.filter(pub => pub.category === category.key).length - 2} more`}
                      </span>
                      {expandedCategories[category.key as keyof typeof expandedCategories] ? <ChevronUpIcon className="h-4 w-4" data-id="element-248" /> : <ChevronDownIcon className="h-4 w-4" data-id="element-249" />}
                    </button>}
                </div>)}
            </div>
          </div>
        </motion.div>

        {/* Audience Insights Column */}
        <motion.div id="audience" variants={itemVariants} className="space-y-8 flex flex-col" data-id="element-250">
          {/* Audience Resonance */}
          <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-200 print:shadow-none print:border print:border-gray-300" data-id="element-251">
            <div className="mb-8" data-id="element-252">
              <p className="text-sm text-gray-500 leading-relaxed mb-4" data-id="element-253">
                <span className="font-semibold text-gray-700" data-id="element-254">
                  Audience Resonance:
                </span>{' '}
                Identifying which interest segments reacted most strongly to the
                campaign stimulus.
              </p>

              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-id="element-255">
                <h2 className="text-xl font-bold text-gray-900" data-id="element-256">
                  Audience Resonance
                </h2>
                <span className="self-start sm:self-auto text-xs font-bold text-[#1E90FF] bg-[#1E90FF]/10 px-3 py-1.5 rounded-full" data-id="element-257">
                  Who responded?
                </span>
              </div>
            </div>

            <div className="space-y-6" data-id="element-258">
              {audienceSegments.map((segment, idx) => <div key={idx} data-id="element-259">
                  <div className="flex items-center justify-between mb-2" data-id="element-260">
                    <div className="flex items-center gap-2" data-id="element-261">
                      <span className="text-sm font-bold text-gray-900" data-id="element-262">
                        {segment.name}
                      </span>
                      {idx === 0 && <span className="text-[10px] font-bold text-white bg-[#1E90FF] px-2 py-0.5 rounded-full" data-id="element-263">
                          TOP SEGMENT
                        </span>}
                    </div>
                    <span className="text-sm font-bold text-[#1E90FF]" data-id="element-264">
                      Index: {segment.index}
                    </span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden" data-id="element-265">
                    <motion.div className="h-3 rounded-full bg-[#1E90FF]" initial={{
                  width: 0
                }} animate={{
                  width: `${segment.index / 250 * 100}%`
                }} transition={{
                  duration: 1,
                  delay: 0.5 + idx * 0.1,
                  ease: 'easeOut'
                }} data-id="element-266" />
                  </div>
                  <div className="flex items-center justify-between mt-2" data-id="element-267">
                    <p className="text-xs text-gray-500 font-medium" data-id="element-268">
                      {segment.index > 100 ? `${segment.index - 100}% above average interest` : 'Average interest'}
                    </p>
                    <p className="text-xs text-gray-400 italic" data-id="element-269">
                      {segment.index > 100 && `${(segment.index / 100).toFixed(1)}x more interested`}
                    </p>
                  </div>
                </div>)}
            </div>

            <div className="mt-8 p-4 bg-gray-50 rounded-lg border border-gray-200 print:bg-white" data-id="element-270">
              <div className="flex items-start gap-3" data-id="element-271">
                <ArrowRightIcon className="h-5 w-5 text-[#1E90FF] mt-0.5 flex-shrink-0" data-id="element-272" />
                <p className="text-sm text-gray-600 leading-relaxed" data-id="element-273">
                  <span className="font-semibold text-gray-900" data-id="element-274">Insight:</span>{' '}
                  The campaign resonated most strongly with users actively
                  interested in{' '}
                  <span className="font-semibold text-[#1E90FF]" data-id="element-275">
                    Pension Planning
                  </span>{' '}
                  (Index 250), confirming the relevance for the 50+ demographic.
                </p>
              </div>
            </div>
          </div>

          {/* Audience Interests Revealed */}
          <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-200 print:shadow-none print:border print:border-gray-300 flex-1" data-id="element-276">
            <div className="mb-8" data-id="element-277">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4" data-id="element-278">
                <h2 className="text-xl font-bold text-gray-900" data-id="element-279">
                  Audience Interests Revealed
                </h2>
                <span className="self-start sm:self-auto text-xs font-bold text-[#1E90FF] bg-[#1E90FF]/10 px-3 py-1.5 rounded-full" data-id="element-280">
                  Who are they?
                </span>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-281">
                Beyond "50+ Mobiel" as a broad demographic, behavioral data
                reveals three distinct sub-profiles based on actual content
                engagement patterns.
              </p>
            </div>

            <div className="space-y-6" data-id="element-282">
              {audiencePersonas.map((persona, idx) => <motion.div key={idx} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.8 + idx * 0.15,
              type: 'spring'
            }} className="relative bg-gray-50 p-6 rounded-xl border border-gray-200" data-id="element-283">
                  <div className="absolute top-4 right-4" data-id="element-284">
                    <div className="px-3 py-1 rounded-full bg-[#1E90FF]/10 border border-[#1E90FF]/20" data-id="element-285">
                      <span className="text-xs font-bold text-[#1E90FF]" data-id="element-286">
                        Index {persona.index}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-start gap-4" data-id="element-287">
                    <div className="flex-shrink-0 p-3 rounded-lg bg-white border border-gray-200" data-id="element-288">
                      {persona.icon === 'wine' && <WineIcon className="h-5 w-5 text-gray-700" data-id="element-289" />}
                      {persona.icon === 'newspaper' && <NewspaperIcon className="h-5 w-5 text-gray-700" data-id="element-290" />}
                      {persona.icon === 'coins' && <CoinsIcon className="h-5 w-5 text-gray-700" data-id="element-291" />}
                    </div>

                    <div className="flex-1 pr-12" data-id="element-292">
                      <h3 className="text-base font-bold text-gray-900 mb-1" data-id="element-293">
                        {persona.name}
                      </h3>
                      <p className="text-xs font-medium mb-2 text-gray-600" data-id="element-294">
                        {persona.tagline}
                      </p>
                      <p className="text-xs text-gray-600 leading-relaxed mb-3" data-id="element-295">
                        {persona.description}
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-296">
                        {persona.interests.slice(0, 3).map((interest, i) => <span key={i} className="text-[10px] px-2 py-1 rounded-md bg-white border border-gray-200 text-gray-700 font-medium" data-id="element-297">
                            {interest}
                          </span>)}
                      </div>
                    </div>
                  </div>
                </motion.div>)}
            </div>

            <div className="mt-8 p-4 bg-[#1E90FF]/5 rounded-lg border border-[#1E90FF]/20" data-id="element-298">
              <div className="flex items-start gap-3" data-id="element-299">
                <LightbulbIcon className="h-5 w-5 text-[#1E90FF] flex-shrink-0 mt-0.5" data-id="element-300" />
                <div data-id="element-301">
                  <p className="text-sm font-bold text-gray-900 mb-2" data-id="element-302">
                    Strategic Implication
                  </p>
                  <p className="text-sm text-gray-600 leading-relaxed" data-id="element-303">
                    The strongest resonance lies with{' '}
                    <span className="font-semibold text-gray-900" data-id="element-304">
                      The Smart Investor
                    </span>{' '}
                    (Index 250) and{' '}
                    <span className="font-semibold text-gray-900" data-id="element-305">
                      The Bon Vivant
                    </span>{' '}
                    (Index 185). Future campaigns should position 50+ Mobiel as
                    the smart choice that frees up money for what matters most,
                    whether that's securing your family's financial future
                    through investments and planning, or enjoying the lifestyle
                    you've earned through travel and experiences. This audience
                    values financial intelligence: they're not just saving
                    money, they're strategically reallocating it toward their
                    priorities.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Key Insight Section */}
      <motion.div variants={itemVariants} className="bg-white p-8 rounded-xl shadow-sm border border-gray-200 print:shadow-none print:border print:border-gray-300 print:break-inside-avoid" data-id="element-306">
        <div className="flex flex-col lg:flex-row items-start justify-between gap-6 mb-8" data-id="element-307">
          <div className="flex items-start gap-4 flex-1" data-id="element-308">
            <LightbulbIcon className="h-7 w-7 text-[#1E90FF] flex-shrink-0 mt-1" data-id="element-309" />
            <h2 className="text-xl md:text-2xl font-bold text-gray-900" data-id="element-310">
              Key Insight: TV Built Lasting Brand Equity, Not Just Temporary
              Awareness
            </h2>
          </div>
          <ShareButton title="Key Insight: TV Built Lasting Brand Equity, Not Just Temporary Awareness" text="The 50+ Mobiel campaign didn't just drive a +33% spike—it created a sustained +14% lift that persisted weeks after the campaign ended. This represents a structural investment in brand value that compounds over time, with 97.5% certainty." data-id="element-311" />
        </div>

        <div className="space-y-5 text-base text-gray-600 leading-relaxed" data-id="element-312">
          <p data-id="element-313">
            This test validates TV as a{' '}
            <span className="font-semibold text-gray-900" data-id="element-314">
              brand-building engine
            </span>
            , not just an awareness channel. The campaign generated a{' '}
            <span className="font-semibold text-gray-900" data-id="element-315">
              validated +33% uplift during flight
            </span>
            , and importantly, brand interest remained{' '}
            <span className="font-semibold text-gray-900" data-id="element-316">
              +14% elevated post-flight
            </span>
            , a promising indicator of lasting brand equity. This structural
            lift means every future Euro spent starts from a higher efficiency
            level. It's not just marketing cost; it's{' '}
            <span className="font-semibold text-gray-900" data-id="element-317">
              capital investment in brand value
            </span>{' '}
            that compounds over time.
          </p>

          <p data-id="element-318">
            The data reveals two critical insights: First, we detected{' '}
            <span className="font-semibold text-gray-900" data-id="element-319">
              zero "warm-up period"
            </span>
            , the curve spiked immediately at campaign launch. This validates
            that the creative message ("Eindejaarsknallers") was extremely clear
            and urgent to the target audience. Second, the strongest resonance
            came from{' '}
            <span className="font-semibold text-gray-900" data-id="element-320">
              The Smart Investor persona
            </span>{' '}
            (Index 250), users actively researching pension planning and
            financial optimization. This audience isn't just saving money;
            they're strategically reallocating it toward their priorities.
          </p>

          <p data-id="element-321">
            With{' '}
            <span className="font-semibold text-gray-900" data-id="element-322">
              97.5% lift certainty
            </span>{' '}
            and statistical validation (p &lt; 0.05), we can confidently say
            this campaign didn't just drive temporary interest, it fundamentally
            shifted how the 50+ market perceives 50+ Mobiel. The strategic
            opportunity now is to maintain this elevated baseline while
            optimizing creative and frequency to maximize ROI on future flights.
          </p>
        </div>
      </motion.div>

      {/* Recommendations & Next Steps */}
      <motion.div variants={itemVariants} className="bg-white p-8 rounded-xl shadow-sm border border-gray-200 print:shadow-none print:border print:border-gray-300 print:break-inside-avoid" data-id="element-323">
        <div className="flex items-start gap-4 mb-8" data-id="element-324">
          <SparklesIcon className="h-7 w-7 text-[#1E90FF] flex-shrink-0 mt-1" data-id="element-325" />
          <h2 className="text-xl md:text-2xl font-bold text-gray-900" data-id="element-326">
            Recommendations & Next Steps
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 print:gap-4" data-id="element-327">
          {[{
          icon: CoinsIcon,
          title: 'Protect Brand Equity',
          text: 'Maintain consistent TV presence to preserve the +14% elevated baseline. Set up continuous measurement to track brand equity evolution and optimize maintenance frequency.',
          note: 'Extending post-flight observation to 3-4 weeks in your next test increases statistical power and improves the likelihood of reaching 90-95% confidence on sustained impact.'
        }, {
          icon: SparklesIcon,
          title: 'Optimize Flight Strategy',
          text: 'The zero warm-up period suggests opportunity to test shorter, more frequent bursts vs. sustained flights to maximize efficiency and ROI. Consider pre-testing creative variants to identify the strongest performer before launch.',
          link: 'https://create.brandpulse-analytics.com/?type=3#uploadVideo',
          linkText: 'pre-testing creative variants'
        }, {
          icon: UsersIcon,
          title: 'Target Smart Investor',
          text: 'Focus on The Smart Investor persona (Index 250) with messaging that positions 50+ Mobiel as financially intelligent. Test persona-specific creative variants in Q2.'
        }].map((rec, idx) => <div key={idx} className="bg-blue-50/50 p-6 rounded-xl border border-blue-200 print:bg-white print:border print:border-gray-300 h-full flex flex-col" data-id="element-328">
              <div className="flex items-center gap-3 mb-4" data-id="element-329">
                <div className="p-2.5 bg-white rounded-lg shadow-sm print:shadow-none" data-id="element-330">
                  <rec.icon className="h-5 w-5 text-[#1E90FF]" data-id="element-331" />
                </div>
                <h3 className="text-base font-bold text-gray-900" data-id="element-332">
                  {rec.title}
                </h3>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed mb-3 flex-grow" data-id="element-333">
                {rec.link ? <>
                    {rec.text.split(rec.linkText!)[0]}
                    <a href={rec.link} target="_blank" rel="noopener noreferrer" className="text-[#1E90FF] hover:underline font-medium" data-id="element-334">
                      {rec.linkText}
                    </a>
                    {rec.text.split(rec.linkText!)[1]}
                  </> : rec.text}
              </p>
              {rec.note && <p className="text-xs text-gray-500 italic bg-white p-3 rounded-lg border border-blue-200 mt-auto" data-id="element-335">
                  <span className="font-semibold" data-id="element-336">Future opportunity:</span>{' '}
                  {rec.note}
                </p>}
            </div>)}
        </div>

        <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 print:bg-white print:border print:border-gray-300" data-id="element-337">
          <p className="text-base text-gray-600 leading-relaxed mb-4" data-id="element-338">
            This test proves TV created a{' '}
            <span className="font-semibold text-gray-900" data-id="element-339">
              structural investment in brand value
            </span>
            , not just temporary awareness. The sustained +14% post-flight
            effect represents brand equity that compounds over time. With
            validated campaign lift and observed post-flight durability, protect
            this gain through consistent presence, amplify what works by
            targeting The Smart Investor persona, and optimize efficiency
            through strategic testing. For your next flight, consider{' '}
            <a href="https://create.brandpulse-analytics.com/?type=3#uploadVideo" target="_blank" rel="noopener noreferrer" className="text-[#1E90FF] hover:underline font-medium" data-id="element-340">
              pre-testing creative concepts
            </a>{' '}
            to predict which will deliver the highest brand resonance.
          </p>
          <div className="flex items-center justify-end" data-id="element-341">
            <div className="flex items-center gap-2 text-sm" data-id="element-342">
              <ShieldCheckIcon className="h-4 w-4 text-[#1E90FF]" data-id="element-343" />
              <span className="text-gray-600 font-medium" data-id="element-344">
                {metrics.confidence}% Lift Certainty
              </span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Confidence Notification */}
      <ConfidenceNotification uplift={metrics.uplift} confidence={metrics.confidence} data-id="element-345" />
    </motion.div>;
};